import React, { forwardRef } from 'react';
import AvatarMui from '@mui/material/Avatar';
import type { SxProps, Theme } from '@mui/material/styles';

import PersonIcon from '@mui/icons-material/Person';

interface AvatarProps {
  src?: string | null;
  // Accessible name for the image. `alt` is what MUI documents and what most
  // call sites pass; `name` is the older alias this wrapper introduced. Either
  // one becomes the image's alt text.
  alt?: string;
  name?: string;
  // Square size in px — shorthand for an sx width/height pair.
  size?: number;
  sx?: SxProps<Theme>;
}

// Only these props are declared because only these are honoured: the wrapper
// forwards nothing else to MUI, and adding a `...rest` spread would silently
// change behaviour at all twelve call sites.
const Avatar = forwardRef<HTMLDivElement, AvatarProps>(({ src, name, alt, size, sx }, ref) => {
  // Array form, not `{...sx}`: an sx given as an array or a theme callback does
  // not survive an object spread.
  const avatarSx: SxProps<Theme> = [!!size && { width: size, height: size }, ...(Array.isArray(sx) ? sx : [sx])];
  // Most call sites pass `alt`, which the untyped version dropped on the floor,
  // so every avatar announced itself to a screen reader as "avatar".
  const label = alt || name || 'avatar';

  return src ? (
    <AvatarMui src={src} alt={label} sx={avatarSx} ref={ref} />
  ) : (
    <AvatarMui alt={label} sx={avatarSx} ref={ref}>
      <PersonIcon sx={{ width: '70%', height: '70%' }} />
    </AvatarMui>
  );
});

Avatar.displayName = 'Avatar';

export default Avatar;
